import { jwtDecode } from "jwt-decode";
import { decrypt } from "../utils/other";

const getTokenObj = () => decrypt(localStorage.getItem("user"));

/**
 * True only for cy-sso access tokens.
 *
 * cy-sso tokens carry `accessTokenSecretId` in their JWT payload (see cy-sso
 * `generateAccessToken`); legacy pre-cy-sso tokens do not. Token migration has
 * been removed, so the API client uses this to detect a legacy session and log
 * the user out — they re-login and get a fresh cy-sso token. A malformed /
 * undecodable token is treated as non-cy-sso (→ logout), which is the safe
 * default since it can't be used anyway.
 */
const isCySsoToken = (token) => {
  try {
    return !!jwtDecode(token)?.accessTokenSecretId;
  } catch {
    return false;
  }
};

/** Issued-at (seconds) of a JWT, or null when absent/undecodable. */
const tokenIat = (token) => {
  try {
    const iat = jwtDecode(token)?.iat;
    return typeof iat === "number" ? iat : null;
  } catch {
    return null;
  }
};

/**
 * True when `candidate` was issued STRICTLY LATER than `reference`. Used to
 * decide whether a token found in a sibling store (shared cookie / another
 * tab's localStorage write) supersedes the one we hold — cy-sso mints every
 * token server-side, so `iat` is a reliable single-clock ordering. An
 * undecodable candidate is never newer; an undecodable reference always loses.
 */
const isNewerToken = (candidate, reference) => {
  const candIat = tokenIat(candidate);
  if (candIat === null) return false;
  const refIat = tokenIat(reference);
  if (refIat === null) return true;
  return candIat > refIat;
};

/** Role claim carried in a cy-sso JWT ("student" / "mentor" / …), or null. */
const tokenRole = (token) => {
  try {
    return jwtDecode(token)?.role || null;
  } catch {
    return null;
  }
};

/** The user id a token belongs to, or null. */
const tokenUserId = (token) => {
  try {
    const d = jwtDecode(token) || {};
    return d.userId || d.studentId || d.sub || null;
  } catch {
    return null;
  }
};

export { getTokenObj, isCySsoToken, tokenIat, isNewerToken, tokenRole, tokenUserId };
